import React, { useEffect, useRef } from 'react';
import { Play, Target, TrendingUp, Zap, Award, ArrowRight, CheckCircle, BarChart3, Sparkles, Star, Users, Eye, MousePointer, Heart, Share2 } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const MetaAdsPage = () => {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const floatingElementsRef = useRef([]);
  const statsRef = useRef([]);
  const sectionsRef = useRef([]);
  const cardsRef = useRef([]);
  const orbsRef = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    const hero = heroRef.current;
    const floatingElements = floatingElementsRef.current;
    const stats = statsRef.current;
    const sections = sectionsRef.current;
    const cards = cardsRef.current;
    const orbs = orbsRef.current;

    // Floating orbs animation
    orbs.forEach((orb, index) => {
      gsap.to(orb, {
        y: index % 2 === 0 ? -50 : 50,
        x: index % 2 === 0 ? 30 : -30,
        rotation: 360,
        duration: 8 + index * 2,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
      });
    });

    // Hero section animation
    gsap.fromTo(hero.querySelector('.hero-content'),
      { opacity: 0, y: 100, scale: 0.9 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.5,
        ease: "power3.out",
        delay: 0.5
      }
    );

    // Floating elements animation
    floatingElements.forEach((element, index) => {
      gsap.to(element, {
        y: -20,
        rotation: 360,
        duration: 4 + index,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
        delay: index * 0.5
      });
    });

    // Stats counter animation
    const animateStats = () => {
      const targets = [5, 2.8, 400, 133];
      stats.forEach((stat, index) => {
        const counter = { value: 0 };
        gsap.to(counter, {
          value: targets[index],
          duration: 2,
          ease: "power2.out",
          onUpdate: () => {
            stat.textContent = targets[index] < 10 ? counter.value.toFixed(1) : Math.floor(counter.value);
          }
        });
      });
    };

    // Stats section scroll trigger
    ScrollTrigger.create({
      trigger: ".stats-section",
      start: "top 80%",
      onEnter: animateStats,
      once: true
    });

    // Sections stagger animation
    sections.forEach((section, index) => {
      gsap.fromTo(section,
        { opacity: 0, y: 80, rotationX: 45 },
        {
          opacity: 1,
          y: 0,
          rotationX: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    // Cards animation
    cards.forEach((card, index) => {
      gsap.fromTo(card,
        { opacity: 0, y: 60, scale: 0.8 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: index * 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 90%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addFloatingRef = (el) => {
    if (el && !floatingElementsRef.current.includes(el)) {
      floatingElementsRef.current.push(el);
    }
  };

  const addStatsRef = (el) => {
    if (el && !statsRef.current.includes(el)) {
      statsRef.current.push(el);
    }
  };

  const addSectionRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  const addCardRef = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  const addOrbRef = (el) => {
    if (el && !orbsRef.current.includes(el)) {
      orbsRef.current.push(el);
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          background: "linear-gradient(180deg, #000 0%, #0a0a0a 50%, #000 100%)",
          color: "#fff",
          overflow: "hidden",
          paddingTop: "0px",
        }}
      >
        {/* Background Orbs */}
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "600px",
            height: "600px",
            background: "radial-gradient(circle, rgba(29, 161, 242, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "10%",
            right: "-15%",
            filter: "blur(120px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(225, 48, 108, 0.06) 0%, transparent 70%)",
            borderRadius: "50%",
            bottom: "20%",
            left: "-10%",
            filter: "blur(100px)",
            zIndex: 1,
          }}
        />
        <div
          ref={addOrbRef}
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(131, 58, 180, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            filter: "blur(80px)",
            zIndex: 1,
          }}
        />

        {/* Floating Particles */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.1), transparent),
              radial-gradient(2px 2px at 40px 70px, rgba(29, 161, 242, 0.1), transparent),
              radial-gradient(1px 1px at 90px 40px, rgba(225, 48, 108, 0.1), transparent),
              radial-gradient(1px 1px at 130px 80px, rgba(131, 58, 180, 0.1), transparent)
            `,
            backgroundRepeat: "repeat",
            backgroundSize: "150px 100px",
            animation: "particleFloat 20s linear infinite",
            zIndex: 1,
          }}
        />

        {/* Floating Background Elements */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
          }}
        >
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "20%",
              left: "10%",
              width: "60px",
              height: "60px",
              background: "rgba(29, 161, 242, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1DA1F2",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(29, 161, 242, 0.2)",
            }}
          >
            <Users size={24} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "30%",
              right: "15%",
              width: "70px",
              height: "70px",
              background: "rgba(225, 48, 108, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#E1306C",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(225, 48, 108, 0.2)",
            }}
          >
            <Eye size={32} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "60%",
              left: "5%",
              width: "65px",
              height: "65px",
              background: "rgba(131, 58, 180, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#833AB4",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(131, 58, 180, 0.2)",
            }}
          >
            <MousePointer size={28} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "70%",
              right: "20%",
              width: "55px",
              height: "55px",
              background: "rgba(29, 161, 242, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1DA1F2",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(29, 161, 242, 0.2)",
            }}
          >
            <Heart size={26} />
          </div>
          <div
            ref={addFloatingRef}
            style={{
              position: "absolute",
              top: "40%",
              left: "80%",
              width: "68px",
              height: "68px",
              background: "rgba(225, 48, 108, 0.1)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#E1306C",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(225, 48, 108, 0.2)",
            }}
          >
            <Share2 size={30} />
          </div>
        </div>

        {/* Hero Section */}
        <section
          ref={heroRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "8rem 2rem 6rem",
            textAlign: "center",
          }}
        >
          <div
            className="hero-content"
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            {/* Hero Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.75rem 1.5rem",
                background: "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(29, 161, 242, 0.4)",
                borderRadius: "50px",
                fontSize: "0.95rem",
                fontWeight: "600",
                marginBottom: "3rem",
                color: "#fff",
                boxShadow: "0 4px 15px rgba(29, 161, 242, 0.15)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: "-50%",
                  width: "50%",
                  height: "100%",
                  background: "linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
                  transform: "skewX(-20deg)",
                  animation: "shimmer 3s infinite",
                }}
              />
              <Sparkles size={18} style={{ color: "#1DA1F2" }} />
              Meta Ads Excellence
            </div>

            <h1
              style={{
                fontSize: "clamp(3rem, 6vw, 5rem)",
                fontWeight: "900",
                lineHeight: "1.1",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              From Feeds to Funnels with{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #1DA1F2, #E1306C, #833AB4)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 3s ease-in-out infinite",
                }}
              >
                Meta Ads
              </span>
            </h1>

            <p
              style={{
                fontSize: "1.3rem",
                fontWeight: "600",
                color: "#1DA1F2",
                marginBottom: "1.5rem",
              }}
            >
              Meta Ads Excellence by Beyond the Analysis
            </p>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "900px",
                margin: "0 auto 3rem",
              }}
            >
              Your ideal customers are already scrolling. The challenge? Getting them to stop, engage, and convert. 
              With nearly 5 billion people on social media, Meta Ads give you the power to connect, but it takes more 
              than visibility to make an impact. Our Meta specialists craft scroll-stopping creative and laser-targeted 
              campaigns that turn attention into action—building brands, driving leads, and scaling revenue.
            </p>
                   <Link to="/consultation">
            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #1DA1F2, #E1306C)",
                color: "#fff",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 8px 25px rgba(29, 161, 242, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(29, 161, 242, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(29, 161, 242, 0.3)";
              }}
            >
              <Play size={24} />
              Turn Scrolls Into Sales
              <ArrowRight size={24} />
            </button>
                  </Link>
          </div>
        </section>

        {/* Stats Section */}
        <section
          className="stats-section"
          style={{
            position: "relative",
            zIndex: 10,
            padding: "4rem 2rem",
            background: "rgba(255, 255, 255, 0.02)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            margin: "0 2rem 6rem",
            maxWidth: "1200px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(29, 161, 242, 0.5), transparent)",
            }}
          />
          
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "3rem",
              textAlign: "center",
            }}
          >
            {[
              { number: 5, label: "Billion Active Users", color: "#1DA1F2" },
              { number: 2.8, label: "Billion Daily Facebook Users", color: "#E1306C" },
              { number: 400, label: "% Average ROAS Increase", color: "#833AB4" },
              { number: 24, label: "Campaigns Completed", color: "#1DA1F2" },
            ].map((stat, index) => (
              <div key={index} style={{ position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    top: "-20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "40px",
                    height: "40px",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Star size={20} style={{ color: "#000" }} />
                </div>
                <div
                  ref={addStatsRef}
                  style={{
                    fontSize: "3rem",
                    fontWeight: "900",
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}80)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    marginBottom: "0.5rem",
                    marginTop: "1rem",
                  }}
                >
                  0
                </div>
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why Meta Ads Matter */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(225, 48, 108, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(225, 48, 108, 0.15)",
            }}
          >
            <Target size={18} style={{ color: "#E1306C" }} />
            Strategic Importance
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            Why Meta Ads{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #E1306C, #833AB4)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Matter
            </span>
          </h2>

          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "24px",
              padding: "3rem",
              marginBottom: "3rem",
            }}
          >
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                marginBottom: "2rem",
                maxWidth: "800px",
                margin: "0 auto 2rem",
              }}
            >
              Meta Ads give your business direct access to nearly 5 billion active users across Facebook, Instagram, 
              Messenger, and the Audience Network. Unlike search ads that wait for intent, Meta Ads create demand by 
              catching users in discovery mode—when they're most open to new ideas and brands.
            </p>
            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "800px",
                margin: "0 auto",
              }}
            >
              With powerful targeting based on interests, behaviors, and lookalike audiences, plus real-time creative 
              testing and optimization, Meta Ads help you turn attention into action—boosting awareness, driving leads, 
              and increasing sales across the entire customer journey.
            </p>
          </div>
                 <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #E1306C, #833AB4)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(225, 48, 108, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Start Reaching the Right Audience
            <ArrowRight size={20} />
          </button>
                </Link>
        </section>

        {/* Who This Is For */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(131, 58, 180, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(131, 58, 180, 0.15)",
            }}
          >
            <Award size={18} style={{ color: "#833AB4" }} />
            Who We Work Best With
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "1rem",
              color: "#fff",
            }}
          >
            Who This Is{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #833AB4, #1DA1F2)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              For
            </span>
          </h2>

          <p
            style={{
              fontSize: "1.2rem",
              fontWeight: "600",
              color: "#833AB4",
              marginBottom: "2rem",
            }}
          >
            We work best with brands that:
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
              marginBottom: "4rem",
            }}
          >
            {[
              "You're a DTC brand doing $30K–$300K/month",
              "You've hit a ROAS ceiling with your current agency",
              "You have a great product but poor ad creative",
              "You're looking for a true growth partner",
            ].map((achievement, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "1.5rem",
                  background: "rgba(255, 255, 255, 0.02)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "16px",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.transform = "translateX(10px)";
                  e.currentTarget.style.borderColor = "rgba(131, 58, 180, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                  e.currentTarget.style.transform = "translateX(0)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "linear-gradient(135deg, #833AB4, #1DA1F2)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle size={20} style={{ color: "#000" }} />
                </div>
                <span
                  style={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {achievement}
                </span>
              </div>
            ))}
          </div>
                <Link to="/consultation">
          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #833AB4, #1DA1F2)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(131, 58, 180, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Find Out If We're a Fit
            <ArrowRight size={20} />
          </button>
                </Link>
        </section>

        {/* 5-Part Scaling System */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(29, 161, 242, 0.4)",
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "3rem",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(29, 161, 242, 0.15)",
            }}
          >
            <TrendingUp size={18} style={{ color: "#1DA1F2" }} />
            Scaling Strategy
          </div>

          <h2
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "2rem",
              color: "#fff",
            }}
          >
            Our 5-Part Scaling{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #1DA1F2, #E1306C)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              System
            </span>
          </h2>

          <p
            style={{
              fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
              color: "rgba(255, 255, 255, 0.8)",
              lineHeight: "1.7",
              maxWidth: "900px",
              margin: "0 auto 4rem",
            }}
          >
            We don't gamble with your ad budget. We scale with structure, strategy, and speed. 
            Our proven system turns creative testing into predictable growth, ensuring every dollar works harder.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "2rem",
            }}
          >
            {[
              {
                icon: Target,
                title: "Fix the Leaks First",
                description: "We begin with a full audit of your pixel, attribution setup, and sales funnel—because scaling only works when nothing's leaking.",
                color: "#1DA1F2",
              },
              {
                icon: BarChart3,
                title: "Build the Right Campaign Structure",
                description: "We separate top, middle, and bottom-of-funnel campaigns with clear budget splits and goals, ensuring each stage is covered.",
                color: "#E1306C",
              },
              {
                icon: Zap,
                title: "Test Creatives Like a Machine",
                description: "Expect 9–12 new variations each week. We use UGC, meme-style, hook-driven, and problem-solution formats to find top performers fast.",
                color: "#833AB4",
              },
              {
                icon: TrendingUp,
                title: "Scale Smart, Not Wild",
                description: "No guesswork. We use rule-based scaling, duplicate winning ad sets, and only increase what's working.",
                color: "#1DA1F2",
              },
              {
                icon: Award,
                title: "Optimize Relentlessly",
                description: "From audience breakdowns to landing page tweaks, we constantly fine-tune to squeeze every drop of performance.",
                color: "#E1306C",
              },
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  ref={addCardRef}
                  style={{
                    position: "relative",
                    background: "rgba(255, 255, 255, 0.02)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "24px",
                    padding: "2.5rem",
                    cursor: "pointer",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-10px) scale(1.02)";
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.borderColor = `${service.color}40`;
                    e.currentTarget.style.boxShadow = `0 20px 40px ${service.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "1px",
                      background: `linear-gradient(90deg, transparent, ${service.color}80, transparent)`,
                    }}
                  />

                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      background: `linear-gradient(135deg, ${service.color}, ${service.color}80)`,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "2rem",
                      color: "#000",
                      boxShadow: `0 8px 25px ${service.color}40`,
                    }}
                  >
                    <IconComponent size={32} />
                  </div>

                  <h3
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "700",
                      marginBottom: "1rem",
                      color: "#fff",
                      lineHeight: "1.3",
                    }}
                  >
                    {service.title}
                  </h3>
                  <p
                    style={{
                      color: "rgba(255, 255, 255, 0.7)",
                      lineHeight: "1.7",
                      fontSize: "1rem",
                    }}
                  >
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
                 <Link to="/consultation">
          <button
            style={{
              marginTop: "4rem",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 2.5rem",
              background: "linear-gradient(135deg, #1DA1F2, #E1306C)",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(29, 161, 242, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            See the System in Action
            <ArrowRight size={20} />
          </button>
          </Link>
        </section>

        {/* Final CTA */}
        <section
          ref={addSectionRef}
          style={{
            position: "relative",
            zIndex: 10,
            padding: "6rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "32px",
              padding: "4rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "2px",
                background: "linear-gradient(90deg, transparent, rgba(29, 161, 242, 0.8), rgba(225, 48, 108, 0.8), transparent)",
              }}
            />

            <h2
              style={{
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: "900",
                lineHeight: "1.2",
                marginBottom: "2rem",
                color: "#fff",
              }}
            >
              Ready to Turn Scrolls into{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #1DA1F2, #E1306C, #833AB4)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 3s ease-in-out infinite",
                }}
              >
                Sales?
              </span>
            </h2>

            <p
              style={{
                fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                color: "rgba(255, 255, 255, 0.8)",
                lineHeight: "1.7",
                maxWidth: "700px",
                margin: "0 auto 3rem",
              }}
            >
              Book a free consultation with our Meta Ads experts. We'll audit your current campaigns and show you 
              exactly how to build scroll-stopping creative that converts browsers into buyers.
            </p>
                   <Link to="/consultation">
            <button
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1.5rem 3rem",
                background: "linear-gradient(135deg, #1DA1F2, #E1306C, #833AB4)",
                backgroundSize: "200% 100%",
                color: "#fff",
                border: "none",
                borderRadius: "50px",
                fontSize: "1.2rem",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                boxShadow: "0 10px 30px rgba(29, 161, 242, 0.3)",
                animation: "gradientShift 3s ease-in-out infinite",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px) scale(1.05)";
                e.currentTarget.style.boxShadow = "0 20px 50px rgba(29, 161, 242, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(29, 161, 242, 0.3)";
              }}
            >
              <Play size={24} />
              Get Your Free Meta Ads Audit
              <ArrowRight size={24} />
            </button>
                  </Link>
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes particleFloat {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-100px); }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes shimmer {
          0% { left: -50%; }
          100% { left: 120%; }
        }

        @media (max-width: 768px) {
          section {
            padding: 3rem 1rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default MetaAdsPage;